import { useParams, Link } from 'react-router-dom';
import { videos, articles } from '@/mocks/data';
import ArticleCard from '@/components/feature/ArticleCard';
import { useEffect, useState } from 'react';

const categoryNames: Record<string, string> = {
  dianying: '电影',
  dianshiju: '电视剧',
  zongyi: '综艺',
  dongman: '动漫',
  jilupian: '纪录片',
  tiyu: '体育',
  yinyue: '音乐',
  youxi: '游戏',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '1', 10);

  const video = videos.find((v) => v.id === articleId);
  const article = articles.find((a) => a.id === articleId);

  const [relatedVideos, setRelatedVideos] = useState<typeof videos>([]);
  const [relatedArticles, setRelatedArticles] = useState<typeof articles>([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [articleId]);

  useEffect(() => {
    if (video) {
      setRelatedVideos(videos.filter((v) => v.category === video.category && v.id !== video.id).slice(0, 4));
    }
    if (article) {
      setRelatedArticles(articles.filter((a) => a.id !== article.id).slice(0, 4));
    }
  }, [video, article]);

  if (!video && !article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
            <i className="ri-error-warning-line text-foreground-500 text-3xl"></i>
          </div>
          <h2 className="text-xl font-bold text-foreground-100 font-heading mb-2">内容未找到</h2>
          <p className="text-sm text-foreground-500 mb-6">您访问的内容不存在或已被移除</p>
          <Link
            to="/"
            className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-foreground-50 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  // Video detail view
  if (video) {
    const catName = categoryNames[video.category] || video.category;

    return (
      <>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'VideoObject',
              name: video.title,
              description: video.description,
              thumbnailUrl: video.cover,
              uploadDate: `${video.year}-01-01`,
              contentRating: video.rating,
              genre: video.tags,
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.yingtao-video.com/' },
                { '@type': 'ListItem', position: 2, name: catName, item: `https://www.yingtao-video.com/category/${video.category}` },
                { '@type': 'ListItem', position: 3, name: video.title, item: `https://www.yingtao-video.com/article/${video.id}` },
              ],
            }),
          }}
        />

        <div className="bg-background-100 border-b border-background-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-sm" aria-label="面包屑导航">
              <Link to="/" className="text-foreground-400 hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-foreground-600 text-xs"></i>
              <Link to={`/category/${video.category}`} className="text-foreground-400 hover:text-primary-400 transition-colors cursor-pointer">{catName}</Link>
              <i className="ri-arrow-right-s-line text-foreground-600 text-xs"></i>
              <span className="text-foreground-200 font-medium truncate max-w-[200px]">{video.title}</span>
            </nav>
          </div>
        </div>

        <main className="bg-background-50 min-h-screen">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Main content */}
              <article className="flex-1 min-w-0">
                <div className="relative rounded-xl overflow-hidden bg-background-200 aspect-video">
                  <img
                    src={video.cover}
                    alt={video.title}
                    title={`${video.title} - 樱桃视频免费高清在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-center justify-center">
                    <div className="w-20 h-20 flex items-center justify-center rounded-full bg-primary-500/90 shadow-lg cursor-pointer hover:bg-primary-600 transition-colors">
                      <i className="ri-play-fill text-foreground-50 text-3xl ml-1"></i>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <Link
                      to={`/category/${video.category}`}
                      className="text-xs text-accent-400 bg-accent-500/10 px-3 py-1 rounded-full whitespace-nowrap cursor-pointer hover:bg-accent-500/20 transition-colors"
                    >
                      {catName}
                    </Link>
                    {video.tags.map((tag) => (
                      <span key={tag} className="text-xs text-foreground-500 bg-background-200/70 px-3 py-1 rounded-full whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <h1 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading">
                    {video.title}
                  </h1>

                  <div className="flex items-center gap-4 mt-3 text-sm text-foreground-500 flex-wrap">
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-400"></i>
                      <span className="text-accent-400 font-medium">{video.rating}</span>
                    </div>
                    <span>{video.year}</span>
                    <span>{video.duration}</span>
                    <span>{video.views}次播放</span>
                  </div>

                  <div className="mt-6 p-5 bg-background-100 rounded-xl border border-background-200/40">
                    <h3 className="text-sm font-semibold text-foreground-200 mb-2">内容简介</h3>
                    <p className="text-sm text-foreground-400 leading-relaxed">{video.description}</p>
                  </div>
                </div>

                {relatedVideos.length > 0 && (
                  <section className="mt-10">
                    <h3 className="text-base font-semibold text-foreground-100 mb-4 flex items-center gap-2">
                      <div className="w-1 h-5 rounded-full bg-primary-500"></div>
                      相关推荐
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {relatedVideos.map((v) => (
                        <Link key={v.id} to={`/article/${v.id}`} className="block">
                          <div className="relative h-32 rounded-lg overflow-hidden bg-background-200 group cursor-pointer">
                            <img src={v.cover} alt={v.title} className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105" />
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/80">
                                <i className="ri-play-fill text-foreground-50 text-lg ml-0.5"></i>
                              </div>
                            </div>
                          </div>
                          <p className="text-xs text-foreground-300 mt-2 line-clamp-2 group-hover:text-primary-400 transition-colors">{v.title}</p>
                        </Link>
                      ))}
                    </div>
                  </section>
                )}
              </article>

              {/* Sidebar */}
              <aside className="lg:w-80 flex-shrink-0">
                <div className="sticky top-24">
                  <h3 className="text-sm font-semibold text-foreground-200 mb-4 flex items-center gap-2">
                    <i className="ri-fire-fill text-primary-400"></i>
                    热门推荐
                  </h3>
                  <div className="flex flex-col gap-3">
                    {videos.filter((v) => v.isHot && v.id !== video.id).slice(0, 6).map((v) => (
                      <Link
                        key={v.id}
                        to={`/article/${v.id}`}
                        className="flex gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer group"
                      >
                        <div className="w-28 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
                          <img src={v.cover} alt={v.title} className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-foreground-200 line-clamp-2 group-hover:text-primary-400 transition-colors leading-snug">{v.title}</p>
                          <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-600">
                            <span className="text-accent-400">{v.rating}</span>
                            <span>{v.views}次播放</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </main>
      </>
    );
  }

  // Article detail view
  if (article) {
    const catName = categoryNames[article.category] || article.category;
    const articleContent = `在当今这个信息爆炸的时代，影视内容已经成为人们日常生活中不可或缺的一部分。${article.title}这个话题近期引发了广泛讨论。本文将为您深入解析其中的精彩内容。

随着中国影视产业的蓬勃发展，越来越多优质内容不断涌现。从剧本创作到后期制作，从演员表演到导演手法，每一个环节都凝聚着创作者的心血。

${article.excerpt}

在当前的市场环境下，观众对于影视作品的要求越来越高。不仅需要精彩的剧情，还需要深刻的主题和精湛的制作水准。而${article.title}无疑满足了这些需求。

从专业角度来看，这部作品的制作团队在细节把控上做足了功夫。无论是场景搭建、服装道具，还是光影运用、音效处理，都达到了行业领先水平。

观众们普遍认为，这类作品的走红并非偶然。它反映了当下社会文化消费的升级趋势，也展示了中国影视产业的勃勃生机。我们有理由相信，未来会有更多优秀的作品涌现。

最后，樱桃视频将持续为您带来最新的影视资讯和深度解读。如果您喜欢这篇文章，欢迎关注我们的平台，获取更多精彩内容。`;

    return (
      <>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              description: article.excerpt,
              image: article.cover,
              author: { '@type': 'Person', name: article.author },
              datePublished: article.date,
              publisher: { '@type': 'Organization', name: '樱桃视频' },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.yingtao-video.com/' },
                { '@type': 'ListItem', position: 2, name: catName, item: `https://www.yingtao-video.com/category/${article.category}` },
                { '@type': 'ListItem', position: 3, name: article.title, item: `https://www.yingtao-video.com/article/${article.id}` },
              ],
            }),
          }}
        />

        <div className="bg-background-100 border-b border-background-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-sm" aria-label="面包屑导航">
              <Link to="/" className="text-foreground-400 hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-foreground-600 text-xs"></i>
              <Link to={`/category/${article.category}`} className="text-foreground-400 hover:text-primary-400 transition-colors cursor-pointer">{catName}</Link>
              <i className="ri-arrow-right-s-line text-foreground-600 text-xs"></i>
              <span className="text-foreground-200 font-medium truncate max-w-[200px]">{article.title}</span>
            </nav>
          </div>
        </div>

        <main className="bg-background-50 min-h-screen">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <article>
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <Link
                    to={`/category/${article.category}`}
                    className="text-xs text-accent-400 bg-accent-500/10 px-3 py-1 rounded-full whitespace-nowrap cursor-pointer hover:bg-accent-500/20 transition-colors"
                  >
                    {catName}
                  </Link>
                  {article.tags.map((tag) => (
                    <span key={tag} className="text-xs text-foreground-500 bg-background-200/70 px-3 py-1 rounded-full whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading leading-tight">
                  {article.title}
                </h1>

                <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500 flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-primary-500/20 flex items-center justify-center">
                      <i className="ri-user-line text-primary-400 text-xs"></i>
                    </div>
                    <span>{article.author}</span>
                  </div>
                  <span>{article.date}</span>
                  <span>{article.readTime} 阅读</span>
                  <span>{article.views} 次浏览</span>
                </div>
              </div>

              <div className="rounded-xl overflow-hidden bg-background-200 mb-8">
                <img
                  src={article.cover}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-auto object-cover object-top"
                />
              </div>

              <div className="prose-custom text-foreground-300 leading-relaxed text-sm md:text-base space-y-4">
                {articleContent.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="text-foreground-300 leading-loose">{paragraph}</p>
                ))}
              </div>

              {/* Tags & Share */}
              <div className="mt-10 pt-6 border-t border-background-200/60">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-foreground-500">标签：</span>
                    {article.tags.map((tag) => (
                      <span key={tag} className="text-xs text-foreground-400 bg-background-200/70 px-3 py-1 rounded-full cursor-pointer hover:bg-background-300/70 transition-colors whitespace-nowrap">
                        #{tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-500">分享：</span>
                    <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-500/30 transition-colors cursor-pointer">
                      <i className="ri-wechat-line text-foreground-400 text-sm"></i>
                    </button>
                    <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-500/30 transition-colors cursor-pointer">
                      <i className="ri-weibo-line text-foreground-400 text-sm"></i>
                    </button>
                    <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-500/30 transition-colors cursor-pointer">
                      <i className="ri-link text-foreground-400 text-sm"></i>
                    </button>
                  </div>
                </div>
              </div>
            </article>

            {relatedArticles.length > 0 && (
              <section className="mt-12">
                <h3 className="text-base font-semibold text-foreground-100 mb-5 flex items-center gap-2">
                  <div className="w-1 h-5 rounded-full bg-primary-500"></div>
                  相关文章
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {relatedArticles.map((a) => (
                    <ArticleCard key={a.id} article={a} variant="horizontal" />
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>
      </>
    );
  }

  return null;
}